<template>
  <b-card-actions
    ref="edit_card"
    :title="$t('Document')"
    action-refresh
    @refresh="refresh"
  >

    <b-form @submit.prevent>
      <!-- Form content with validation providers -->

      <b-row>

        <b-col cols="12">
          <b-form-group label-for="v-description">
            <label v-html="getRequiredLabel('description')" />
            <b-form-input
              id="v-description"
              v-model="document.description"
              required
              :disabled="editDisabled"
              :placeholder="$t('description')"
            />

            <span class="text-danger">{{ errors['v-description'] }}</span>
          </b-form-group>

        </b-col>

      </b-row>
      <b-row>

        <b-dropdown
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          :text="currentDocumentSection.name"
          variant="outline-primary"
          class="button-inline-spacing-2"
          leng
          :disabled="editDisabled"
        >
          <b-dropdown-item
            v-for="document_section in document_sections"
            :key="document_section.id"
            @click="setSection(document_section)"
          >
            <span class="ml-50">{{ document_section.name }}</span>
          </b-dropdown-item>

        </b-dropdown>

      </b-row>
      <spacer
        v-if="!isNew"
        height="50"
      />

      <b-row v-if="!isNew">
        <b-col
          v-if="!isNew"
          cols="4"
        >
          <b-form-group
            :label="$t('revision')"
            label-for="v-revision"
          >
            <b-form-input
              id="v-revision"
              v-model="document.revision"
              :disabled="editDisabled"
              :placeholder="$t('revision')"
            />
          </b-form-group>
        </b-col>

        <b-col
          v-if="!showUploadFile"
          cols="8"
        >
          <b-form-group
            :label="$t('Actions')"
            label-for="v-actions"
          >
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="outline-primary"
              class="btn-icon"
              :disabled="editDisabled"
              @click.prevent.stop="enableUploadNewVersion($event)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="outline-primary"
              class="btn-icon button-inline-spacing-2"
              @click="download()"
            >
              <feather-icon :icon="iconType" />
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="!isNew">

        <!-- revision date-->
        <b-col cols="4">
          <b-form-group
            :label="$t('revision')"
            label-for="v-revisioned-at"
          >

            <flat-pickr
              id="v-revisioned-at"
              v-model="document.revisioned_at"
              class="form-control"
              :disabled="editDisabled"
              :config="{dateFormat: 'd/m/Y', allowInput: true}"
            />
          </b-form-group>

        </b-col>

      </b-row>
      <spacer height="50" />

      <b-row>

        <!-- upload -->
        <b-col
          v-if="showUploadFile"
          cols="12"
        >
          <b-form-group
            :label="uploadLabel"
            label-for="v-upload"
          >

            <b-form-file
              id="file-upload"
              v-model="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.jpg,.png,.gif"
              :placeholder="$t('Click to upload')"
            />
          </b-form-group>
        </b-col>

      </b-row>

      <b-row>

        <!-- upload -->
        <b-col
          v-if="showUploadFile"
          cols="12"
        >
          <b-form-group
            :label="linkLabel"
            label-for="v-upload"
          >

            <b-form-input
              id="v-link"
              v-model="document.link"
              :disabled="editDisabled"
              :placeholder="$t('https://')"
            />
          </b-form-group>
        </b-col>

      </b-row>
      <b-row>
        <b-col
          v-if="showUploadFile"
          cols="12"
          class="mb-3"
        >
          <span class="text-danger">{{ errors['fileOrLink'] }}</span>
        </b-col>
      </b-row>

      <!-- Manual Revision Controls when uploading -->
      <b-row v-if="showUploadFile">
        <b-col cols="12">
          <b-form-group>
            <b-form-checkbox
              v-model="manualRevisionControl"
              :disabled="editDisabled"
              switch
              class="mb-3"
            >
              {{ $t('Manual revision control') }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="showUploadFile && manualRevisionControl">
        <b-col cols="6">
          <b-form-group
            :label="$t('Revision number')"
            label-for="v-revision-new"
          >
            <b-form-input
              id="v-revision-new"
              v-model="newRevisionNumber"
              type="number"
              min="1"
              :disabled="editDisabled"
              :placeholder="$t('Revision number')"
            />
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            :label="$t('Revision date')"
            label-for="v-revision-date-new"
          >
            <flat-pickr
              id="v-revision-date-new"
              v-model="newRevisionDate"
              class="form-control"
              :disabled="editDisabled"
              :config="{dateFormat: 'd/m/Y', allowInput: true}"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>

        <spacer height="50" />
        <!-- submit and reset -->
        <b-col cols="12">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            class="mr-1"
            :disabled="editDisabled"
            @click.prevent.stop="handleSubmit($event)"
          >
            {{ $t('Save') }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="reset"
            variant="outline-secondary"
            @click.prevent.stop="back($event)"
          >
            {{ $t('Cancel') }}

          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card-actions>
</template>

<script>
import {
  BButton,
  BCard,
  BCardHeader,
  BCardTitle,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormCheckbox,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import UserService from '@/basic/userService'
import useJwt from '@/auth/jwt/useJwt'
import store from '@/store'
import { BCardActions } from '@core/components/b-card-actions'
import i18n from '@/libs/i18n'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { environment } from '@/auth/environment'
import ability from '@/libs/acl/ability'
import flatPickr from 'vue-flatpickr-component'
import { ValidationObserver, ValidationProvider } from 'vee-validate'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BForm,
    BButton,
    BCard,
    BFormTextarea,
    BTable,
    BCardHeader,
    BCardTitle,
    BCardActions,
    BFormFile,
    BDropdown,
    BDropdownItem,
    flatPickr,

  },
  directives: {
    Ripple,
  },
  data() {
    return {
      company_users: [],
      permission_fields: [{ key: 'name', label: i18n.t('Name') }, { key: 'read', label: i18n.t('Read') }, {
        key: 'write',
        label: i18n.t('Write'),
      }],
      permissionsData: [],
      customMessages: {
        required: i18n.t('This field is required'),
      },
      errors: [],
      userData: JSON.parse(localStorage.getItem('userData')),
      userCompany: JSON.parse(localStorage.getItem('userCompany')),
      editDisabled: !ability.can('manage', 'Basic Library'),
      revisionDisabled: true,
      file: null,
      filedata: null,
      document_sections: [{ id: this.$route.params.section_id, name: '' }],
      document_section_id_new: this.$route.params.section_id,
      document_id: this.$route.params.id,
      document: {
        description: '', revision: 1, revisioned_at: '', document_section_id: this.$route.params.section_id,
      },

      uploadNewVersion: false,
      manualRevisionControl: false,
      newRevisionNumber: null,
      newRevisionDate: null,
    }
  },
  computed: {
    showUploadFile() {
      return this.document_id === 'new' || this.uploadNewVersion
    },
    iconType() {
      return this.document.link && this.document.link.trim() !== '' ? 'GlobeIcon' : 'DownloadIcon'
    },
    isNew() {
      return this.document_id === 'new'
    },
    linkLabel() {
      return i18n.t('Or paste here a link')
    },
    uploadLabel() {
      if (this.document_id === 'new') {
        return i18n.t('Upload revision')
      }

      if (this.manualRevisionControl && this.newRevisionNumber) {
        return `${i18n.t('Upload revision')} (${this.newRevisionNumber})`
      }

      return `${i18n.t('Upload revision')} (${this.document.revision + 1})`
    },
    currentDocumentSection() {
      console.log('currentDocumentSection document_section_id', this.document.document_section_id)
      console.log('currentDocumentSection document_sections', this.document_sections)
      if (this.document && this.document_sections && this.document_section_id_new) {
        const section = this.document_sections.find(l => l.id == this.document_section_id_new)
        console.log('currentDocumentSection section', section)
        return section
        // return {id: "1", name: "alfa"}
      }
      return { id: '-11', name: 'beta' }
      // return this.userCompany;
    },
  },
  mounted() {
    this.refresh()
    // this.$http.get('/good-table/basic')
    // .then(res => { this.rows = res.data })
  },
  methods: {
    refresh() {
      this.uploadNewVersion = false

      const userService = new UserService(useJwt)
      const document_id = this.$route.params.id
      console.log('refresh document id', document_id)

      this.$refs.edit_card.showLoading = true

      if (document_id === 'new') {
        console.log('refresh new document id', document_id)

        this.document.description = ''
        this.document.revision = 1
      } else {
        console.log('refresh not new document id', document_id)

        userService.document(document_id, {}).then(response => {
          this.document = response.data.data.document
          this.document_section_id_new = this.document.document_section_id
          console.log('document', response.data.data.document)
        })
      }
      const company_id = store.state.userCompany.id
      console.log('refresh document_sections for company id', company_id)

      userService.document_sections(company_id, {}).then(response => {
        this.document_sections = response.data.data
        this.$refs.edit_card.showLoading = false
      })
    },
    setSection(section) {
      this.document_section_id_new = section.id
      // this.refresh()
    },
    enableUploadNewVersion(section) {
      this.uploadNewVersion = true
      // Set default values for manual revision control
      this.newRevisionNumber = this.document.revision ? this.document.revision + 1 : 1
      this.newRevisionDate = new Date().toLocaleDateString('it-IT') // DD/MM/YYYY format

      // this.refresh()
    },
    getRequiredLabel(field) {
      return `<span class="text-danger">*</span> ${i18n.t(field)}`
    },
    download() {
      console.log('document', document)
      console.log('this document', this.document)

      const link = document.createElement('a')

      // Check for a valid link
      if (this.document.link && this.document.link.trim()) {
        link.href = this.document.link
        link.target = '_blank' // Open in a new tab
      }
      // Check for a valid attachment
      else if (this.document.attachment && this.document.attachment.path && environment && environment.baseURL) {
        link.href = `${environment.baseURL}/documents/download_attachment/${this.document.attachment.id}/${this.document.attachment.otp}`
        link.download = this.document.attachment.path
      }
      // Handle case where neither link nor attachment is available
      else {
        console.error('No link or attachment available for download')
        return
      }

      // Perform the action (open link or download)
      document.body.appendChild(link) // Append to body to ensure it works in all browsers
      link.click()
      document.body.removeChild(link) // Clean up

      // Revoke the URL only for downloads
      if (this.document.attachment && this.document.attachment.path) {
        URL.revokeObjectURL(link.href)
      }
    },

    updateDocument(userService, data) {
      userService.update_document(this.document_id, data).then(response => {
        if (response.data.status === 'success') {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: i18n.t('Document updated'),
              icon: 'AlertIcon',
              variant: 'success',
              text: i18n.t('Document saved with success'),
            },
          })
          this.$router.push({ name: 'documents', params: { document_section_id: this.document.document_section_id } })
        } else {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: i18n.t('Generic error'),
              icon: 'AlertIcon',
              variant: 'danger',
              text: i18n.t('There was an error saving the document, please retry'),
            },
          })
          this.$refs.edit_card.showLoading = true
        }
      })
    },
    async back(e) {
      this.$router.push({ name: 'documents', params: { document_section_id: this.document.document_section_id } })
    },

    validateForm() {
      const inputs = this.$el.querySelectorAll('input[required], textarea[required], select[required]')
      let isValid = true

      // Reset errors
      this.errors = []

      // Validate regular required fields
      inputs.forEach(input => {
        if (!input.value || input.value.trim() === '') {
          isValid = false
          this.errors[input.id] = i18n.t('This field is required')
        }
      })

      // Custom validation for file upload or link
      if (isValid && !this.file && (!this.document.link || this.document.link.trim() === '')) {
        isValid = false
        this.errors.fileOrLink = i18n.t('Either a file must be uploaded or a link must be provided')
      }

      return isValid
    },
    async handleSubmit(e) {
      if (this.validateForm()) {
        // Form is valid, proceed with submission
        console.log('Form submission successful')
        this.submitForm()
      } else {
        // Form is invalid, handle errors
        console.log('Form validation failed', this.errors)
      }
    },

    async submitForm() {
      this.errors = []
      const userService = new UserService(useJwt)
      this.$refs.edit_card.showLoading = true

      this.submitted = true

      console.log('handleSubmit file', this.file)
      console.log('handleSubmit document_section_id_new', this.document_section_id_new)
      console.log('handleSubmit uploadNewVersion', this.uploadNewVersion)
      console.log('handleSubmit document', this.document)
      console.log('handleSubmit document_id', this.document_id)

      this.document.document_section_id = this.document_section_id_new

      // Handle manual revision control
      if (this.manualRevisionControl) {
        this.document.revision = this.newRevisionNumber
        this.document.revisioned_at = this.newRevisionDate
      }

      const data = {
        document: this.document,
        company: this.userCompany,
        uploadNewVersion: this.uploadNewVersion,
        manualRevisionControl: this.manualRevisionControl,
      }

      if (this.file) {
        const filevalue = this.file
        console.log('handleSubmit filevalue', filevalue)

        const filePath = filevalue.name
        console.log('handleSubmit filePath', filevalue.name)

        const reader = new FileReader()

        reader.onload = e => {
          console.log('readAsDataURL', e.target.result)
          this.filedata = e.target.result
          const data_upload = {
            filedata: this.filedata,
            filename: filePath,
          }

          userService.upload_attachment(this.userCompany.id, data_upload).then(response => {
            if (response.data.status === 'success') {
              console.log('response', response.data)

              this.document.attachment_id = response.data.data.attachment.id
              this.updateDocument(userService, data)
            } else {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: i18n.t('Generic error'),
                  icon: 'AlertIcon',
                  variant: 'danger',
                  text: i18n.t('There was an error saving the attachment, please retry'),
                },
              })
            }
            this.refresh()
          })
        }
        reader.readAsDataURL(this.file)
      } else {
        this.updateDocument(userService, data)
      }

      this.$refs.edit_card.showLoading = false
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
